import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

interface SEOProps {
  title: string;
  description: string;
  keywords: string;
  canonicalPath: string;
  ogType?: string;
  jsonLd?: Record<string, unknown>;
}

export default function usePageSEO({
  title,
  description,
  keywords,
  canonicalPath,
  ogType = 'website',
  jsonLd,
}: SEOProps) {
  const location = useLocation();

  useEffect(() => {
    document.title = title;

    const setMeta = (name: string, content: string, isProperty = false) => {
      const attr = isProperty ? 'property' : 'name';
      let el = document.querySelector(`meta[${attr}="${name}"]`);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, name);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };

    setMeta('description', description);
    setMeta('keywords', keywords);

    const canonicalUrl = `https://www.renrentv.com${canonicalPath}`;
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', canonicalUrl);

    setMeta('og:title', title, true);
    setMeta('og:description', description, true);
    setMeta('og:type', ogType, true);
    setMeta('og:url', canonicalUrl, true);

    setMeta('twitter:title', title);
    setMeta('twitter:description', description);

    const ldScriptId = 'page-jsonld';
    const existingLd = document.getElementById(ldScriptId);
    if (existingLd) {
      existingLd.remove();
    }
    if (jsonLd) {
      const script = document.createElement('script');
      script.id = ldScriptId;
      script.type = 'application/ld+json';
      script.textContent = JSON.stringify(jsonLd);
      document.head.appendChild(script);
    }

    return () => {
      const ld = document.getElementById(ldScriptId);
      if (ld) ld.remove();
    };
  }, [title, description, keywords, canonicalPath, ogType, jsonLd, location.pathname]);
}